import { ModalId } from '@heidi/schema/ml-scribe';
import { useQueuedModal } from '@heidi/utils';
import { useAtom, useSetAtom } from 'jotai';
import dynamic from 'next/dynamic';
import { useEffect } from 'react';
import {
  isReferralProgramAvailableForLocale,
  shouldShowAffiliateLinkModalAtom,
  trackReferralLinkCopied,
} from '../../data';

import { useIntl } from 'react-intl';
import { isAffiliateLinkModalOpenAtom } from '../referrals/affiliate-link-modal.atoms';

type AffiliateLinkModalProps = {
  className?: string;
  isEvidenceOnlyUser: boolean;
  invalidateCurrentUser: () => Promise<void>;
  user?: {
    email?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    referral_profile?: { referral_link?: string | null } | null;
  };
  isOpen?: boolean;
};

const AffiliateLinkModalImpl = dynamic(
  () =>
    import('./affiliate-link-modal-impl').then((module) => module.AffiliateLinkModalImpl),
  { ssr: false }
);

const AffiliateLinkModal = ({
  className,
  isEvidenceOnlyUser,
  invalidateCurrentUser,
  user,
  isOpen,
}: AffiliateLinkModalProps) => {
  const intl = useIntl();
  const [isManualOpen, setIsManualOpen] = useAtom(isAffiliateLinkModalOpenAtom);
  const setShouldShowAffiliateLinkModal = useSetAtom(shouldShowAffiliateLinkModalAtom);
  const {
    isOpen: isQueueOpen,
    handleClose: dequeueQueuedModal,
    closeWithOptions,
  } = useQueuedModal(ModalId.AFFILIATE_LINK);

  const isReferralAvailable = isReferralProgramAvailableForLocale(intl.locale);
  const shouldBeOpen = isOpen !== undefined ? isOpen : isManualOpen || isQueueOpen;

  const handleClose = () => {
    setIsManualOpen(false);
    setShouldShowAffiliateLinkModal(false);
    dequeueQueuedModal();
  };

  // Locale guard must dequeue — returning null alone leaves AFFILIATE_LINK at the
  // head of the launch queue and blocks every lower-priority modal for the session.
  useEffect(() => {
    if (isReferralAvailable) return;

    setIsManualOpen(false);
    setShouldShowAffiliateLinkModal(false);
    if (isQueueOpen) {
      closeWithOptions({ skipPacing: true, increment_count: false });
    }
  }, [
    isReferralAvailable,
    isQueueOpen,
    closeWithOptions,
    setIsManualOpen,
    setShouldShowAffiliateLinkModal,
  ]);

  if (!shouldBeOpen || !isReferralAvailable) return null;

  return (
    <AffiliateLinkModalImpl
      className={className}
      isEvidenceOnlyUser={isEvidenceOnlyUser}
      invalidateCurrentUser={invalidateCurrentUser}
      user={user}
      onClose={handleClose}
      onReferralLinkCopied={() => trackReferralLinkCopied()}
    />
  );
};

export default AffiliateLinkModal;
